import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { useSettings } from "@/context/SettingsContext";
import { api } from "@/lib/api";
import { LogOut, ShoppingBag, Package, Tag, Sparkles, Building2, Settings as SettingsIcon, Ticket, Images, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const links = [
    { to: "/admin", label: "Orders", icon: ShoppingBag, end: true, testid: "admin-nav-orders" },
    { to: "/admin/products", label: "Products", icon: Package, end: false, testid: "admin-nav-products" },
    { to: "/admin/categories", label: "Categories", icon: Tag, end: false, testid: "admin-nav-categories" },
    { to: "/admin/departments", label: "Departments", icon: Building2, end: false, testid: "admin-nav-departments" },
    { to: "/admin/promos", label: "Promos", icon: Ticket, end: false, testid: "admin-nav-promos" },
    { to: "/admin/bulk-images", label: "Bulk Images", icon: Images, end: false, testid: "admin-nav-bulk-images" },
    { to: "/admin/users", label: "Users", icon: Users, end: false, testid: "admin-nav-users" },
    { to: "/admin/settings", label: "Settings", icon: SettingsIcon, end: false, testid: "admin-nav-settings" },
];

export default function AdminLayout() {
    const { logout } = useAdminAuth();
    const { store_name } = useSettings();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);

    useEffect(() => {
        api.get("/orders/stats").then((r) => setStats(r.data)).catch(() => {});
    }, []);

    const seed = async () => {
        try {
            const { data } = await api.post("/admin/seed");
            toast.success(data.message || "Sample data added");
            window.location.reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not seed");
        }
    };

    return (
        <div className="min-h-screen bg-background text-foreground">
            <header className="border-b border-border/50 bg-background/80 backdrop-blur-md sticky top-0 z-30">
                <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
                    <div>
                        <p data-testid="admin-store-name" className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                            {store_name || "Store"} · Admin
                        </p>
                        <h1 className="font-serif text-2xl">Merchant dashboard</h1>
                    </div>
                    <div className="flex items-center gap-2">                       
                        <Button
                            variant="ghost"
                            size="sm"
                            data-testid="admin-logout-btn"
                            onClick={() => {
                                logout();
                                navigate("/admin");
                            }}
                            className="rounded-full"
                        >
                            <LogOut className="h-4 w-4 mr-1" />
                            Logout
                        </Button>
                    </div>
                </div>
                <nav className="max-w-6xl mx-auto px-6 pb-3 flex gap-1">
                    {links.map(({ to, label, icon: Icon, end, testid }) => (
                        <NavLink
                            key={to}
                            to={to}
                            end={end}
                            data-testid={testid}
                            className={({ isActive }) =>
                                `flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${
                                    isActive
                                        ? "bg-foreground text-background"
                                        : "text-muted-foreground hover:text-foreground hover:bg-surface/60"
                                }`
                            }
                        >
                            <Icon className="h-4 w-4" />
                            {label}
                        </NavLink>
                    ))}
                </nav>
            </header>

            {stats && (
                <section className="max-w-6xl mx-auto px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard label="Orders today" value={stats.today_count} testid="stat-today" />
                    <StatCard label="Pending" value={stats.pending_count} testid="stat-pending" />
                    <StatCard label="Revenue today" value={`₹${stats.revenue_today.toFixed(2)}`} testid="stat-revenue-today" />
                    <StatCard label="Total revenue" value={`₹${stats.revenue_total.toFixed(2)}`} testid="stat-revenue-total" />
                </section>
            )}

            <main className="max-w-6xl mx-auto px-6 pb-16">
                <Outlet />
            </main>
        </div>
    );
}

function StatCard({ label, value, testid }) {
    return (
        <div
            data-testid={testid}
            className="bg-card border border-border/60 rounded-2xl p-5 shadow-sm"
        >
            <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{label}</p>
            <p className="font-serif text-3xl mt-2 text-foreground">{value}</p>
        </div>
    );
}
